import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box, 
  Typography, 
  Container, 
  Paper, 
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  Chip
} from "@mui/material";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TransitEnterexitIcon from '@mui/icons-material/TransitEnterexit';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchOrgChart as GetOrgChartService, fetchOrgChartTypes } from "../../Domain/Services/GetOrgChartService";
import OrgChartNodeEditor from "../Components/OrgChartNodeEditor";
import OrgChartViewBuilder from "./OrgChartViewBuilder";
import * as AdminService from '../../Domain/Services/OrgChartAdminServices';
import { ORGANIGRAMA } from "Shared/Routing/Application/Constants/PathsConstant";
import { useAuthContext } from "Shared/Security/Domain/Contexts/authContext";
import { ADMIN_ORGCHART, ORGCHART } from "Shared/Security/Application/Constants/PermissionsConstant";
import { useNavigate, useSearchParams } from "react-router-dom";

const HIDDEN_COMPANY_CHART_TYPES = ['VACACIONES', 'EXTRAS'];

const AdminOrgChartView = ({
  fixedChartType = null,
  adminTitle = 'Administrador de Organigrama',
  adminDescription = 'Gestiona visualmente la estructura. Cada acción replica un comando de consola.',
  allowNewStructure = true,
  allowStructureRenameDelete = true,
  allowBuilderPanel = true,
  allowChildCreation = true,
  allowEmployeeAssignment = true,
  allowReparenting = true,
  rootButtonLabel = null,
  autoCreateRootTitle = null,
  rootDialogTitle = 'Nuevo Nodo Raíz',
  rootDialogDescription = 'Se creará un nodo en la base del organigrama:',
  backToPath = ORGANIGRAMA,
}) => {
  const navigate = useNavigate();
  const { isAdmin, checkPermission } = useAuthContext();
  const canAdminOrgChart = isAdmin() || checkPermission(ADMIN_ORGCHART) || checkPermission(ORGCHART);

  useEffect(() => {
    if (!canAdminOrgChart) {
      navigate(backToPath || ORGANIGRAMA, { replace: true });
    }
  }, [canAdminOrgChart, navigate, backToPath]);

  const [searchParams, setSearchParams] = useSearchParams();
  const structureQuery = searchParams.get("structure") || searchParams.get("chartType");
  const delegationQuery = searchParams.get("delegation") || searchParams.get("delegationId");
  const [allData, setAllData] = useState([]);
  const [availableChartTypes, setAvailableChartTypes] = useState([]);
  const initialStructure = (structureQuery && structureQuery.toUpperCase() !== "GENERAL")
    ? structureQuery
    : (fixedChartType || "");
  const [selectedChartType, setSelectedChartType] = useState(initialStructure);
  const [loading, setLoading] = useState(true);
  
  // Editor State
  const [editNode, setEditNode] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [autoEditId, setAutoEditId] = useState(null);

  // New Structure State
  const [isNewChartModalOpen, setIsNewChartModalOpen] = useState(false);
  const [newChartName, setNewChartName] = useState('');

  // Root Modal State
  const [isRootModalOpen, setIsRootModalOpen] = useState(false);
  const [newRootTitle, setNewRootTitle] = useState('');

  // Delegation Creation State (MANTENIMIENTO)
  const [isDelegationModalOpen, setIsDelegationModalOpen] = useState(false);
  const [newDelegationName, setNewDelegationName] = useState('');

  // Delegation Tab State (MANTENIMIENTO)
  const [selectedDelegationId, setSelectedDelegationId] = useState(
    delegationQuery ? (Number(delegationQuery) || delegationQuery) : null
  );

  useEffect(() => {
    if (delegationQuery) {
      setSelectedDelegationId(Number(delegationQuery) || delegationQuery);
    }
  }, [delegationQuery]);

  // Navigation & Search State
  const [collapsedIds, setCollapsedIds] = useState(new Set());

  // Panel activo: 'edit' | 'builder'
  const [activePanel, setActivePanel] = useState('edit');

  // Move Node State
  const [movingNodes, setMovingNodes] = useState(new Set());
  const [moveTargetNode, setMoveTargetNode] = useState(null);
  const [isMoveConfirmationOpen, setIsMoveConfirmationOpen] = useState(false);

  // Rename & Delete Structure State
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const getNodeDisplayLabel = (node) => {
    if (!node) {
      return 'Raíz';
    }

    if (node.position && node.user) {
      return `${node.position} • ${node.user}`;
    }

    return node.text || node.position || 'Nodo sin nombre';
  };

  const loadData = (chartType = selectedChartType) => {
    if (!chartType) {
      setAllData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    GetOrgChartService(chartType, {
      includeDetails: true,
    })
      .then(res => {
        const data = res || [];
        setAllData(data);
      })
      .catch(err => {
        console.error("Error fetching org chart:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    fetchOrgChartTypes()
      .then(types => {
        const nextTypes = types || [];
        const hasFixedChartType = fixedChartType && nextTypes.some(
          (type) => (type || '').toUpperCase() === fixedChartType.toUpperCase()
        );
        setAvailableChartTypes(
          fixedChartType && !hasFixedChartType
            ? [...nextTypes, fixedChartType]
            : nextTypes
        );
        if (fixedChartType) {
          setSelectedChartType(fixedChartType);
        } else if (structureQuery && structureQuery.toUpperCase() !== 'GENERAL') {
          const matchedType = nextTypes.find(
            (type) => (type || '').toUpperCase() === structureQuery.toUpperCase()
          );
          if (matchedType) {
            setSelectedChartType(matchedType);
          }
        } else {
          const firstEditable = nextTypes.find(
            (type) => (type || '').toUpperCase() !== 'GENERAL' && !HIDDEN_COMPANY_CHART_TYPES.includes((type || '').toUpperCase())
          );
          if (firstEditable) {
            setSelectedChartType(firstEditable);
          }
        }
      })
      .catch(err => {
        console.error("Error fetching chart types:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fixedChartType, structureQuery]);

  useEffect(() => {
    if (fixedChartType && selectedChartType !== fixedChartType) {
      setSelectedChartType(fixedChartType);
    }
  }, [fixedChartType, selectedChartType]);

  useEffect(() => {
    if (!allowBuilderPanel && activePanel !== 'edit') {
      setActivePanel('edit');
    }
  }, [allowBuilderPanel, activePanel]);

  useEffect(() => {
    const isMatchingQuery = structureQuery && (selectedChartType || '').toUpperCase() === structureQuery.toUpperCase();
    if (isMatchingQuery && delegationQuery) {
      setSelectedDelegationId(Number(delegationQuery) || delegationQuery);
    } else if (!isMatchingQuery && selectedChartType) {
      setSelectedDelegationId(null);
    }
    setCollapsedIds(new Set());
    setMovingNodes(new Set());
    setMoveTargetNode(null);
    setIsMoveConfirmationOpen(false);
    if (!selectedChartType) {
      setAllData([]);
      return;
    }
    loadData(selectedChartType);
  }, [selectedChartType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoEditId && !loading && allData.length > 0) {
      const node = allData.find(n => n.id === autoEditId);
      if (node) {
        setEditNode(node);
        setIsEditorOpen(true);
        setAutoEditId(null);
      }
    }
  }, [allData, loading, autoEditId]);

  const chartTypes = React.useMemo(() => {
    return fixedChartType
      ? [fixedChartType]
      : [...new Set([
          ...availableChartTypes
        ])]
          .filter(type => type && type.toUpperCase() !== 'GENERAL')
          .sort();
  }, [fixedChartType, availableChartTypes]);

  const companyChartTypes = React.useMemo(() => {
    const baseTypes = fixedChartType
      ? chartTypes
      : chartTypes.filter(
          (type) => !HIDDEN_COMPANY_CHART_TYPES.includes((type || '').toUpperCase())
        );

    if (
      selectedChartType &&
      selectedChartType.toUpperCase() !== 'GENERAL' &&
      !HIDDEN_COMPANY_CHART_TYPES.includes(selectedChartType.toUpperCase()) &&
      !baseTypes.some((t) => (t || '').toUpperCase() === selectedChartType.toUpperCase())
    ) {
      return [...baseTypes, selectedChartType].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }

    return baseTypes;
  }, [fixedChartType, chartTypes, selectedChartType]);

  const selectValue = React.useMemo(() => {
    if (!selectedChartType) return '';
    const match = companyChartTypes.find(
      (t) => (t || '').toUpperCase() === selectedChartType.toUpperCase()
    );
    return match || selectedChartType;
  }, [selectedChartType, companyChartTypes]);

  const chartData = allData;

  const chartNodeMap = React.useMemo(() => chartData.reduce((acc, n) => { acc[n.id] = n; return acc }, {}), [chartData]);
  const rootNodes = chartData.filter(n => n.parent === null || n.parent === 0 || !chartNodeMap[n.parent]);
  const rootIds = new Set(rootNodes.map(n => n.id));
  const delegationNodes = chartData.filter(n => rootIds.has(n.parent));
  const isMantenimientoSelected = (selectedChartType || '').toUpperCase() === 'MANTENIMIENTO';
  const showDelegationTabs = isMantenimientoSelected && delegationNodes.length > 0;

  const activeDelegationId = React.useMemo(() => {
    if (!showDelegationTabs) return false;
    const target = selectedDelegationId || delegationQuery;
    const match = delegationNodes.find(node => 
      String(node.id) === String(target) || 
      (node.text && String(target) && node.text.toLowerCase() === String(target).toLowerCase())
    );
    return match ? match.id : (delegationNodes[0]?.id ?? false);
  }, [showDelegationTabs, delegationNodes, selectedDelegationId, delegationQuery]);

  const filteredData = React.useMemo(() => {
    const currentId = activeDelegationId;
    if (!showDelegationTabs || !currentId) return chartData;
    const allowedIds = new Set([currentId]);
    const queue = [currentId];
    while (queue.length > 0) {
      const current = queue.shift();
      chartData.forEach(node => {
        if (node.parent === current && !allowedIds.has(node.id)) {
          allowedIds.add(node.id);
          queue.push(node.id);
        }
      });
    }
    return chartData.filter(node => allowedIds.has(node.id));
  }, [chartData, activeDelegationId, showDelegationTabs]);

  const startParentId = (showDelegationTabs && activeDelegationId)
    ? (chartData.find(n => n.id === activeDelegationId)?.parent ?? null)
    : null;

  // Auto-seleccionar primera delegación si no existe la actual
  useEffect(() => {
    if (!showDelegationTabs || delegationNodes.length === 0) return;
    const target = selectedDelegationId || delegationQuery;
    const match = delegationNodes.find(n => 
      String(n.id) === String(target) || 
      (n.text && String(target) && n.text.toLowerCase() === String(target).toLowerCase())
    );
    if (match) {
      if (String(selectedDelegationId) !== String(match.id)) {
        setSelectedDelegationId(match.id);
      }
    } else if (delegationNodes[0]) {
      setSelectedDelegationId(delegationNodes[0].id);
    }
  }, [showDelegationTabs, delegationNodes, selectedDelegationId, delegationQuery]);

  // Colapsar todo al cambiar de delegación
  useEffect(() => {
    if (!selectedDelegationId) return;
    const idsWithChildren = new Set(
      allData
        .filter(n => (n.chartType || 'General') === selectedChartType)
        .filter(n => allData.some(child => child.parent === n.id))
        .map(n => n.id)
    );
    setCollapsedIds(idsWithChildren);
  }, [selectedDelegationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackToOrgChart = () => {
    const params = new URLSearchParams();
    if (selectedChartType && selectedChartType.toUpperCase() !== "GENERAL") {
      params.set('structure', selectedChartType);
    }
    if (showDelegationTabs && activeDelegationId) {
      params.set('delegation', activeDelegationId);
    }
    const queryString = params.toString();
    const targetPath = queryString ? `${backToPath}?${queryString}` : backToPath;
    navigate(targetPath);
  };
  const handleEditClick = (node) => {
    setEditNode(node);
    setIsEditorOpen(true);
  };

  const handleActionComplete = (newNodeId = null) => {
    if (newNodeId) {
      setAutoEditId(newNodeId);
    }
    loadData(selectedChartType);
  };

  const handleRenameChart = () => {
    setRenameValue(selectedChartType);
    setIsRenameModalOpen(true);
  };

  const confirmRenameChart = async () => {
    const nextName = renameValue.trim();
    if (!nextName || nextName === selectedChartType) {
      setIsRenameModalOpen(false);
      return;
    }

    try {
      await AdminService.renameChartType(selectedChartType, nextName);
      setAvailableChartTypes(prev => prev.map(t => t === selectedChartType ? nextName : t));
      setSelectedChartType(nextName);
      setIsRenameModalOpen(false);
    } catch (err) {
      alert("Error renombrando estructura: " + err.message);
    }
  };

  const handleDeleteChart = () => {
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteChart = async () => {
    try {
      const typeToDelete = selectedChartType;
      await AdminService.deleteChartType(typeToDelete);
      setAvailableChartTypes(prev => prev.filter(t => t !== typeToDelete));
      setSelectedChartType(null);
      setIsDeleteConfirmOpen(false);
    } catch (err) {
      alert("Error eliminando estructura: " + err.message);
    }
  };

  const createRootNode = async (title) => {
    try {
      const res = await AdminService.createNode(title, selectedChartType || "General");
      setIsRootModalOpen(false);
      handleActionComplete(res.id);
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleCreateRoot = () => {
    if (autoCreateRootTitle) {
      createRootNode(autoCreateRootTitle);
      return;
    }

    setNewRootTitle('');
    setIsRootModalOpen(true);
  };

  const handleCreateNewChart = () => {
    setNewChartName('');
    setIsNewChartModalOpen(true);
  };

  const handleRefresh = () => {
    setLoading(true);
    if (fixedChartType) {
      loadData(fixedChartType);
      return;
    }

    fetchOrgChartTypes()
      .then(types => {
        setAvailableChartTypes(types || []);
      })
      .catch(err => {
        console.error("Error refreshing chart types:", err);
      })
      .finally(() => {
        loadData(selectedChartType);
      });
  };

  const confirmCreateNewChart = async () => {
    const name = newChartName.trim();
    if (!name) {
      return;
    }

    const chartTypeAlreadyExists = availableChartTypes.some(
      (type) => (type || '').toUpperCase() === name.toUpperCase()
    );

    if (chartTypeAlreadyExists) {
      alert(`La estructura "${name}" ya existe.`);
      return;
    }

    try {
      const res = await AdminService.createNode(name, name);
      setAvailableChartTypes((prev) => [...prev, name]);
      setSelectedChartType(name);
      setIsNewChartModalOpen(false);
      handleActionComplete(res.id);
    } catch (err) {
      alert('Error creando estructura: ' + err.message);
    }
  };

  const confirmCreateRoot = async () => {
    createRootNode(newRootTitle);
  };

  const handleCreateDelegation = () => {
    setNewDelegationName('');
    setIsDelegationModalOpen(true);
  };

  const confirmCreateDelegation = async () => {
    try {
      const name = newDelegationName.trim();
      if (!name) return;
      // Encontrar el nodo raíz de MANTENIMIENTO
      const maintenanceRoot = allData.find(n => n.parent === null && (n.chartType || 'General') === 'MANTENIMIENTO');
      if (!maintenanceRoot) {
        alert('No se encontró el nodo raíz de MANTENIMIENTO. Créalo primero.');
        return;
      }
      const res = await AdminService.createNode(name, 'MANTENIMIENTO', maintenanceRoot.id);
      setIsDelegationModalOpen(false);
      handleActionComplete(res.id);
    } catch (err) {
      alert("Error creando delegación: " + err.message);
    }
  };

  const toggleNode = (id) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setCollapsedIds(new Set());
  const collapseAll = () => {
    const idsWithChildren = new Set(
      allData
        .filter(n => (n.chartType || "General") === selectedChartType)
        .filter(n => allData.some(child => child.parent === n.id))
        .map(n => n.id)
    );
    setCollapsedIds(idsWithChildren);
  };

  const handleSearchChange = (event, newValue) => {
    if (!newValue) return;
    
    // Ensure ancestors are expanded
    const ancestors = [];
    let current = newValue;
    while (current && current.parent) {
      ancestors.push(current.parent);
      current = allData.find(n => n.id === current.parent);
    }
    
    setCollapsedIds(prev => {
      const next = new Set(prev);
      ancestors.forEach(id => next.delete(id));
      return next;
    });

    // Scroll to element
    setTimeout(() => {
      const element = document.getElementById(`admin-node-${newValue.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.backgroundColor = '#fff9c4';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      }
    }, 100);
  };

  const handleStartMove = (node) => {
    const wasMoving = movingNodes.has(node.id);
    setMovingNodes(prev => {
      const next = new Set(prev);
      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }
      return next;
    });
    if (!wasMoving) {
      setCollapsedIds(prev => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
    }
  };

  const handleSelectMoveTarget = (targetNode) => {
    setMoveTargetNode(targetNode);
    setIsMoveConfirmationOpen(true);
  };

  const confirmMoveNode = async () => {
    try {
      const nodeIds = Array.from(movingNodes);
      await Promise.all(nodeIds.map(id => AdminService.reassignNode(id, moveTargetNode?.id || null)));
      setIsMoveConfirmationOpen(false);
      setMovingNodes(new Set());
      setMoveTargetNode(null);
      handleActionComplete();
    } catch (err) {
      alert("Error moviendo nodos: " + err.message);
    }
  };

  const isDescendant = (parent, potentialDescendantId) => {
    const children = allData.filter(n => n.parent === parent.id);
    if (children.some(c => c.id === potentialDescendantId)) return true;
    for (const child of children) {
      if (isDescendant(child, potentialDescendantId)) return true;
    }
    return false;
  };

  const renderAdminNode = (node) => {
    const isPositionLinked = !!node.positionId;
    const isOfficialPosition = Boolean(node.isOfficialPosition || (node.positionId && node.jobPositionId));
    const isCustomPosition = Boolean(isPositionLinked && !isOfficialPosition);
    const hasPositionAndUser = Boolean(node.position && node.user);
    const hasChildren = filteredData.some(child => child.parent === node.id);
    const isCollapsed = collapsedIds.has(node.id);
    const isMoving = movingNodes.has(node.id);
    const isTargetable = movingNodes.size > 0 && !isMoving &&
      !Array.from(movingNodes).some(mId => {
        const mNode = allData.find(n => n.id === mId);
        return mNode && isDescendant(mNode, node.id);
      });
    const isGroup = !isPositionLinked;

    // Determinar colores y estilo según tipo
    let borderColor = 'grey.300';
    let bgColor = '#ffffff';
    let avatarBg = 'grey.300';
    let avatarIcon = <AccountTreeIcon sx={{ fontSize: 14 }} />;

    if (isMoving) {
      bgColor = '#fffde7';
      borderColor = 'warning.main';
      avatarBg = 'warning.light';
    } else if (isTargetable) {
      bgColor = '#f0fdf4';
      borderColor = 'success.main';
    } else if (isGroup) {
      bgColor = '#f5f5f5';
      borderColor = 'grey.400';
      avatarIcon = <AccountTreeIcon sx={{ fontSize: 14 }} />;
    } else if (isCustomPosition) {
      borderColor = '#ed6c02';
      bgColor = '#fffdf9';
      avatarBg = '#ff9800';
      avatarIcon = <LabelOutlinedIcon sx={{ fontSize: 14 }} />;
    } else {
      borderColor = 'primary.light';
      avatarBg = 'primary.main';
      avatarIcon = <PersonIcon sx={{ fontSize: 14 }} />;
    }

    const actions = !allowReparenting ? (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Editar">
          <IconButton size="small" color="primary" onClick={() => handleEditClick(node)} sx={{ p: 0.4 }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    ) : movingNodes.size > 0 ? (
      isMoving ? (
        <Tooltip title="Quitar de selección">
          <IconButton size="small" color="warning" onClick={() => handleStartMove(node)} sx={{ p: 0.4 }}>
            <CancelIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : isTargetable ? (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Añadir a selección">
            <IconButton size="small" color="warning" onClick={() => handleStartMove(node)} sx={{ p: 0.4 }}>
              <CompareArrowsIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleSelectMoveTarget(node)}
            sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.4, px: 1 }}
          >
            Mover aquí
          </Button>
        </Stack>
      ) : null
    ) : (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Mover">
          <IconButton size="small" color="warning" onClick={() => handleStartMove(node)} sx={{ p: 0.4 }}>
            <CompareArrowsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton size="small" color="primary" onClick={() => handleEditClick(node)} sx={{ p: 0.4 }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    );

    return (
      <Card
        id={`admin-node-${node.id}`}
        key={node.id}
        variant="outlined"
        sx={{
          mb: 1,
          borderLeft: '4px solid',
          borderColor: borderColor,
          transition: 'all 0.2s ease',
          opacity: movingNodes.size > 0 && !isMoving && !isTargetable ? 0.5 : 1,
          bgcolor: bgColor,
          boxShadow: isMoving ? 2 : isTargetable ? 1 : 0,
          '&:hover': {
            boxShadow: 2,
            borderColor: 'primary.light',
          },
        }}
      >
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={() => toggleNode(node.id)}
                  disabled={movingNodes.size > 0}
                  sx={{ p: 0.3, flexShrink: 0 }}
                >
                  {isCollapsed ? (
                    <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 32, flexShrink: 0 }} />
              )}
              <Avatar sx={{ bgcolor: !isPositionLinked ? 'grey.400' : avatarBg, width: 28, height: 28, flexShrink: 0 }}>
                {avatarIcon}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {!hasPositionAndUser && (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', lineHeight: 1.3, mb: 0.3 }}
                    noWrap
                  >
                    {node.text}
                    {isMoving && (
                      <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                        (Moviendo...)
                      </Typography>
                    )}
                  </Typography>
                )}
                {hasPositionAndUser && isMoving && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.3 }}>
                    (Moviendo...)
                  </Typography>
                )}
                {node.position && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2, color: isCustomPosition ? '#d84315' : 'inherit' }} noWrap>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {node.position}
                      </Box>
                      {node.user ? ` • ${node.user}` : ''}
                    </Typography>
                    {isCustomPosition && (
                      <Chip
                        label="Personalizado"
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ height: 16, fontSize: '0.55rem', px: 0.2, py: 0 }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
            {actions}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderHierarchy = (parentId = null, level = 0) => {
    const children = filteredData.filter(n => n.parent === parentId);
    if (children.length === 0) return null;

    return (
      <Box sx={{ ml: level * 2 }}>
        {children.map(node => (
          <Box key={node.id}>
            {renderAdminNode(node)}
            {!collapsedIds.has(node.id) && renderHierarchy(node.id, level + 1)}
          </Box>
        ))}
      </Box>
    );
  };

  if (!canAdminOrgChart) {
    return null;
  }

  return (
    
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* boton de volver a organigrama */}
        <Button 
          variant="contained"
          color="primary" 
          startIcon={<AccountTreeIcon />} 
          onClick={() => handleBackToOrgChart()}
          sx={{ textTransform: 'none', fontSize: '1rem', py: 0, mb: 2 }}
        >
          Volver a Organigrama
        </Button>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTreeIcon /> {adminTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {adminDescription}
            </Typography>
          </Box>
          <Stack direction="column" spacing={1} alignItems="flex-end">
            {allowReparenting && movingNodes.size > 0 ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Alert icon={false} severity="warning" sx={{ py: 0, px: 1, border: '1px solid', borderColor: 'warning.light' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                  MODO MOVER: {movingNodes.size} nodo{movingNodes.size > 1 ? 's' : ''} seleccionado{movingNodes.size > 1 ? 's' : ''}
                </Typography>
                <Typography variant="caption">
                  {Array.from(movingNodes)
                    .map(id => {
                      const nd = allData.find(n => n.id === id);
                      if (!nd) return null;
                      return getNodeDisplayLabel(nd);
                    })
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Alert>
              <Button 
                variant="outlined" 
                color="error" 
                size="small" 
                startIcon={<CancelIcon />} 
                onClick={() => setMovingNodes(new Set())}
                sx={{ textTransform: 'none', py: 0.5 }}
              >
                Cancelar
              </Button>
              {allowReparenting && Array.from(movingNodes).some(id => { const nd = allData.find(n => n.id === id); return nd && nd.parent !== null }) && (
                <Button 
                  variant="contained" 
                  color="info" 
                  size="small" 
                  startIcon={<TransitEnterexitIcon />} 
                  onClick={() => handleSelectMoveTarget(null)}
                  sx={{ textTransform: 'none', py: 0.5 }}
                >
                  Mover a Raíz
                </Button>
              )}
            </Box>
          ) : (
            <Stack direction="row" spacing={1}>
              {showDelegationTabs && (
                <Tooltip title="Crea una nueva delegación bajo MANTENIMIENTO">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddCircleIcon />}
                    onClick={handleCreateDelegation}
                  >
                    Nueva Delegación
                  </Button>
                </Tooltip>
              )}
              {selectedChartType && (
                <Tooltip title={`Crea un nuevo nodo sin padre en el organigrama "${selectedChartType}"`}>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    onClick={handleCreateRoot}
                  >
                    {rootButtonLabel || `Nuevo Nodo Raiz en "${selectedChartType}"`}
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
          </Stack>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Tabs de panel */}
        {allowBuilderPanel && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant={activePanel === 'edit' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<AccountTreeIcon />}
            onClick={() => setActivePanel('edit')}
            sx={{ textTransform: 'none' }}
          >
            Editar Estructuras
          </Button>
          <Button
            variant={activePanel === 'builder' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<UnfoldMoreIcon />}
            onClick={() => setActivePanel('builder')}
            sx={{ textTransform: 'none' }}
          >
            Montar Vista General
          </Button>
        </Stack>
        )}

        {allowBuilderPanel && activePanel === 'builder' && (
          <OrgChartViewBuilder availableChartTypes={chartTypes} />
        )}

        {activePanel === 'edit' && showDelegationTabs && (
          <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeDelegationId}
              onChange={(_, val) => {
                if (val !== null) {
                  setSelectedDelegationId(val);
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('delegation', val);
                  setSearchParams(newParams, { replace: true });
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {delegationNodes.map(node => (
                <Tab key={node.id} label={node.text} value={node.id} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
              ))}
            </Tabs>
          </Box>
        )}

        {activePanel === 'edit' && (<Grid container spacing={3} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
            {fixedChartType ? (
              <TextField
                fullWidth
                size="small"
                label="Estructura"
                value={selectedChartType || fixedChartType}
                InputProps={{ readOnly: true }}
              />
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Estructura</InputLabel>
                  <Select
                    value={selectValue}
                    label="Estructura"
                    onChange={(e) => { setSelectedChartType(e.target.value); setSelectedDelegationId(null) }}
                  >
                    <MenuItem value="">
                      <em>Seleccionar estructura...</em>
                    </MenuItem>
                    {companyChartTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {allowNewStructure && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleCreateNewChart}
                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1 }}
                  >
                    Nueva
                  </Button>
                )}
              </>
            )}
            {allowStructureRenameDelete && selectedChartType && selectedChartType.toUpperCase() !== 'GENERAL' && !fixedChartType && (
              <>
                <Tooltip title="Renombrar estructura">
                  <IconButton size="small" onClick={handleRenameChart} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar estructura completa">
                  <IconButton size="small" onClick={handleDeleteChart} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Autocomplete
              size="small"
              options={filteredData}
              getOptionLabel={(option) => `${option.text} ${option.position ? `- ${option.position}` : ''} ${option.user ? `(${option.user})` : ''}`}
              onChange={handleSearchChange}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Buscar nodo..." 
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refrescar estructura seleccionada">
                <span>
                  <IconButton onClick={handleRefresh} size="small" color="primary" disabled={!selectedChartType || loading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Expandir todo">
                <IconButton onClick={expandAll} size="small" color="primary">
                  <UnfoldMoreIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Contraer todo">
                <IconButton onClick={collapseAll} size="small" color="primary">
                  <UnfoldLessIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>)}

        {activePanel === 'edit' && (loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ bgcolor: '#f5f7fa', p: 2, borderRadius: 2, minHeight: 400 }}>
            {renderHierarchy(startParentId)}
            {!selectedChartType && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ p: 4 }}>
                Selecciona una estructura para cargar sus nodos.
              </Typography>
            )}
            {selectedChartType && filteredData.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ p: 4 }}>
                {`No hay nodos en ${selectedChartType}. Crea uno nuevo para empezar.`}
              </Typography>
            )}
          </Box>
        ))}
      </Paper>

      {/* Editor de Nodo */}
      <OrgChartNodeEditor 
        open={isEditorOpen}
        node={editNode}
        onClose={() => setIsEditorOpen(false)}
        onActionComplete={handleActionComplete}
        allowChildCreation={allowChildCreation}
        allowEmployeeAssignment={allowEmployeeAssignment}
      />

      {/* Modal Nueva Delegación */}
      <Dialog open={isDelegationModalOpen} onClose={() => setIsDelegationModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCircleIcon /> Nueva Delegación
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Se creará una nueva delegación bajo MANTENIMIENTO:
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light', color: 'white', fontWeight: 'bold', mb: 2 }}>
            MANTENIMIENTO
          </Paper>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la Delegación"
            placeholder="Ej: GIRONA, LLEIDA, MADRID..."
            fullWidth
            variant="outlined"
            value={newDelegationName}
            onChange={(e) => setNewDelegationName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsDelegationModalOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmCreateDelegation} variant="contained" color="success" disabled={!newDelegationName.trim()}>
            Crear Delegación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Nuevo Nodo Raíz */}
      {!autoCreateRootTitle && (
      <Dialog open={isRootModalOpen} onClose={() => setIsRootModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCircleIcon /> {rootDialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {rootDialogDescription}
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: 'primary.light', color: 'white', fontWeight: 'bold', mb: 2 }}>
            {selectedChartType}
          </Paper>
          <TextField
            autoFocus
            margin="dense"
            label="Título del Nodo"
            fullWidth
            variant="outlined"
            value={newRootTitle}
            onChange={(e) => setNewRootTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsRootModalOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmCreateRoot} variant="contained" disabled={!newRootTitle.trim()}>
            Crear Nodo
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Modal Nueva Estructura */}
      <Dialog open={isNewChartModalOpen} onClose={() => setIsNewChartModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>Nueva Estructura Organigrama</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Se creará un nuevo nodo raíz para esta estructura bajo GENERAL y quedará disponible como organigrama independiente.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la Estructura"
            placeholder="Ej: IT, FINANZAS, COMERCIAL..."
            fullWidth
            variant="outlined"
            value={newChartName}
            onChange={(e) => setNewChartName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsNewChartModalOpen(false)} color="inherit">Cancelar</Button>
          <Button 
            onClick={confirmCreateNewChart} 
            variant="contained" 
            disabled={!newChartName.trim()}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de Movimiento */}
      <Dialog
        open={isMoveConfirmationOpen}
        onClose={() => setIsMoveConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'white',
              boxShadow: '0 8px 20px rgba(2, 136, 209, 0.28)',
            }}
          >
            <TransitEnterexitIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: '1.05rem' }}>
              Confirmar movimiento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              Reubicar este elemento dentro de una nueva rama del organigrama.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body1" sx={{ mb: 2.5, lineHeight: 1.6, fontSize: '0.95rem' }}>
              ¿Deseas mover {movingNodes.size === 1 ? 'el nodo y crear el hijo dentro de ' : `los ${movingNodes.size} nodos y crear los hijos dentro de `}
              <Box component="span" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                {getNodeDisplayLabel(moveTargetNode)}
              </Box>
              ?
            </Typography>

            <Stack spacing={2.25}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: '#fbfcff',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                  }}
                >
                  Nodo a mover
                </Typography>
                <Stack spacing={1} sx={{ maxHeight: 180, overflow: 'auto' }}>
                  {Array.from(movingNodes).map(id => {
                    const nd = allData.find(n => n.id === id);
                    if (!nd) return null;
                    return (
                      <Box
                        key={id}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderRadius: 2,
                          bgcolor: 'white',
                          borderLeft: '4px solid',
                          borderColor: 'warning.main',
                          boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.35, fontSize: '0.92rem' }}>
                          {getNodeDisplayLabel(nd)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="stretch">
                <Box
                  sx={{
                    width: 52,
                    minWidth: 52,
                    borderRadius: 2.5,
                    bgcolor: '#f3f7fb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <CompareArrowsIcon sx={{ color: 'primary.main' }} />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #e9f7ef 0%, #f7fffa 100%)',
                    border: '1px solid',
                    borderColor: 'success.light',
                    boxShadow: '0 10px 24px rgba(34, 197, 94, 0.08)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      color: 'success.dark',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      fontWeight: 700,
                      fontSize: '0.72rem',
                    }}
                  >
                    Destino nuevo (padre)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: 'success.dark', lineHeight: 1.35, fontSize: '0.95rem' }}>
                    {getNodeDisplayLabel(moveTargetNode)}
                  </Typography>
                </Box>
              </Stack>

              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  alignItems: 'center',
                  '& .MuiAlert-message': { width: '100%' },
                  '& .MuiAlert-message, & .MuiAlert-icon': { fontSize: '0.9rem' },
                }}
              >
                Al confirmar, el elemento y toda su descendencia pasarán a depender de este nuevo padre.
              </Alert>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.25, borderTop: '1px solid', borderColor: 'grey.200' }}>
          <Button onClick={() => setIsMoveConfirmationOpen(false)} color="inherit" sx={{ px: 2.5 }}>
            Cancelar
          </Button>
          <Button onClick={confirmMoveNode} variant="contained" color="primary" sx={{ px: 3, fontWeight: 700 }}>
            Confirmar Movimiento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Renombrar Estructura */}
      <Dialog open={isRenameModalOpen} onClose={() => setIsRenameModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>Renombrar Estructura</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Nuevo nombre"
            fullWidth
            variant="outlined"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsRenameModalOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmRenameChart} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Sugerencia de Borrado */}
      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon /> ¿Eliminar esta estructura?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Estás a punto de borrar la estructura <strong>{selectedChartType}</strong> y TODOS sus nodos hijos de forma permanente.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer. Se borrarán todos los puestos vinculados a este organigrama.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsDeleteConfirmOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmDeleteChart} variant="contained" color="error">Eliminar TODO</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

AdminOrgChartView.propTypes = {
  fixedChartType: PropTypes.string,
  adminTitle: PropTypes.string,
  adminDescription: PropTypes.string,
  allowNewStructure: PropTypes.bool,
  allowStructureRenameDelete: PropTypes.bool,
  allowBuilderPanel: PropTypes.bool,
  allowChildCreation: PropTypes.bool,
  allowEmployeeAssignment: PropTypes.bool,
  allowReparenting: PropTypes.bool,
  rootButtonLabel: PropTypes.string,
  autoCreateRootTitle: PropTypes.string,
  rootDialogTitle: PropTypes.string,
  rootDialogDescription: PropTypes.string,
  backToPath: PropTypes.string,
};

export default AdminOrgChartView;
